import { useAuth0, User } from "@auth0/auth0-react";
import { MissingRefreshTokenError } from "@auth0/auth0-spa-js";
import { useCallback, useMemo } from "react";
import {
  getTenantSlugFromOrgName,
} from "~/lib/tenant-routing";
import {
  getContextualLoginPath,
  isRecoverableAuthError,
  logoutWithSafeReturnTo,
  normalizePostLogoutPath,
  recoverFromBrokenAuthSession,
} from "~/lib/auth-recovery";

// Custom namespace for Auth0 roles
const ROLES_NAMESPACE = "https://proudly-human.com/roles";

export type AuthUser = User & {
  email?: string;
  email_verified?: boolean;
  [ROLES_NAMESPACE]?: string[];
  // Organization claims (when user is logged in via an organization)
  org_id?: string;
  org_name?: string;
};

export function useAuth0Login() {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0<AuthUser>();

  const login = useCallback(
    (
      screenHint?: "signup" | "login",
      options?: { organizationId?: string; invitation?: string; loginHint?: string; returnTo?: string; forcePrompt?: boolean }
    ) =>
      loginWithRedirect({
        authorizationParams: {
          ...(screenHint && { screen_hint: screenHint }),
          ...(options?.organizationId && { organization: options.organizationId }),
          ...(options?.invitation && { invitation: options.invitation }),
          ...(options?.loginHint && { login_hint: options.loginHint }),
          ...(options?.forcePrompt && { prompt: "login" }),
        },
        appState: {
          returnTo:
            options?.returnTo ??
            (screenHint === "signup" ? "/dashboard?new=true" : "/dashboard"),
        },
      }),
    [loginWithRedirect]
  );

  const logoutToHome = useCallback(
    (options?: { returnTo?: string }) =>
      logoutWithSafeReturnTo({
        logout,
        postLogoutPath: options?.returnTo
          ? normalizePostLogoutPath(options.returnTo)
          : undefined,
      }),
    [logout]
  );

  // Organization context
  const orgId = user?.org_id;
  const orgName = user?.org_name;
  const orgSlug = getTenantSlugFromOrgName(orgName);
  const isOrgMember = !!orgId;

  const getToken = useCallback(async (): Promise<string> => {
    const startTime = performance.now();

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
      });

      const duration = performance.now() - startTime;

      // If it took more than 100ms, it likely made a network request (used refresh token)
      // Cached tokens return almost instantly (<10ms)
      if (duration > 100) {
        console.log(`[Auth] Token refreshed (took ${duration.toFixed(0)}ms - used refresh token)`);
      }

      return token;
    } catch (error) {
      // Handle missing refresh token — redirect to login preserving any invite params
      if (error instanceof MissingRefreshTokenError) {
        console.warn("[Auth] Missing refresh token, redirecting to login...");
        const params = new URLSearchParams(window.location.search);
        const invitation = params.get("invitation");
        const organization = params.get("organization");
        const loginPath = getContextualLoginPath();

        if (invitation && organization) {
          // Invite flow: redirect to /login with invite params so login.tsx can handle it
          const loginParams = new URLSearchParams();
          loginParams.set("invitation", invitation);
          loginParams.set("organization", organization);
          const orgName = params.get("organization_name");
          if (orgName) loginParams.set("organization_name", orgName);
          window.location.href = `${loginPath}?${loginParams.toString()}`;
        } else {
          // Route back through the correct login page for the current context.
          window.location.href = loginPath;
        }
        throw error;
      }

      // Handle Auth0 rejecting stale/revoked refresh tokens (403 / access_denied).
      // Before triggering a full recovery (which logs the user out), try to
      // use the cached access token. The refresh may fail (e.g. due to Auth0
      // refresh token rotation/reuse detection) while the access token is
      // still valid.
      if (isRecoverableAuthError(error)) {
        try {
          const cachedToken = await getAccessTokenSilently({
            authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
            cacheMode: "cache-only",
          });
          if (cachedToken) {
            return cachedToken;
          }
        } catch {
          // Cache miss or expired — proceed with recovery
        }

        console.warn(
          "[Auth] Broken Auth0 session detected, clearing site data and logging out..."
        );
        await recoverFromBrokenAuthSession({ logout });

        throw error;
      }

      throw error;
    }
  }, [getAccessTokenSilently, logout]);

  const roles = useMemo(() => user?.[ROLES_NAMESPACE] ?? [], [user]);

  const hasRole = useCallback(
    (role: string): boolean => {
      return roles.includes(role);
    },
    [roles]
  );

  return {
    isAuthenticated,
    isLoading,
    user,
    roles,
    hasRole,
    login,
    logout: logoutToHome,
    getToken,
    // Organization support
    orgId,
    orgName,
    orgSlug,
    isOrgMember,
  };
}
